import {useEffect, useMemo, useRef, useState} from "react";
import {useHookClass} from "@dvrt/graphql-widgets";
import {DataSource} from "./dataSource";
import {Form, FormValues} from "./form";
import {useBoolState} from "@/hooks";
import {Divider, IconButton, Menu, MenuItem} from "@mui/material";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import Loading from "@/components/breadcrumbs/Loading";
import {AddNewMemberDialog} from "./AddNewMemberDialog";
import {MemberRow} from "./MemberRow";
import {useStyles} from "./styles";
import {FormattedMessage} from "react-intl";
import {FormattedButton, FormattedAlert, FormActionButtons} from "@dvrt/graphql-widgets";
import {FormattedPageTitle} from "@/components/pages";
import {showNotification} from "@/actions";
import {useDispatch} from "react-redux";
import {defineMessage} from "react-intl";
import {
    DndContext,
    DragEndEvent,
    MouseSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {restrictToParentElement, restrictToVerticalAxis} from "@dnd-kit/modifiers";
import {SortableContext, useSortable, verticalListSortingStrategy} from "@dnd-kit/sortable";
import {moveItemsInArray} from "@/tools";
import {Capability} from "@/api";
import {CSS} from "@dnd-kit/utilities";

const callGroupMembersTitle = defineMessage({
    id: "callGroupMembers",
    defaultMessage: "Members of {name} ({memberCount}/{size})",
});

const callQueueMembersTitle = defineMessage({
    id: "callQueueMembers",
    defaultMessage: "Members of {name}",
});

type SectionDividerRowProps = {
    active: boolean;
    idx: number;
    openNewMemberDialog: (afterIndex: number) => void;
    onSectionChanged: (type: "add" | "remove", sectionIndex: number) => void;
};

const SectionDividerRow = ({active, idx, openNewMemberDialog, onSectionChanged}: SectionDividerRowProps) => {
    const {setNodeRef, attributes, transform, transition} = useSortable({id: `section-${idx}`});
    const {classes, cx} = useStyles();
    const [anchorEl, setAnchorEl] = useState(null);

    const closeMenu = () => setAnchorEl(null);

    const draggableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <tr className={classes.sectionRow} ref={setNodeRef} style={draggableStyle} {...attributes}>
            <td colSpan={100}>
                <Divider className={cx(classes.sectionDivider, {[classes.activeDivider]: active})} />
                <FontAwesomeIcon
                    className={cx(classes.sectionMenu, {[classes.activeSectionMenu]: active})}
                    icon="map-marker"
                    rotation={90}
                    size="2x"
                    onClick={(ev) => setAnchorEl(ev.currentTarget)}
                />
                <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={closeMenu}>
                    {!active && (
                        <>
                            <MenuItem
                                onClick={() => {
                                    // We want to add the new member after the member before this section, but
                                    // the idx is the index of the member this section is before
                                    // (so it starts with 1, not 0)
                                    // therefore we need to subtract 1 to get the index of the member
                                    // after which we want to add the new member
                                    openNewMemberDialog(idx - 1);
                                    closeMenu();
                                }}
                            >
                                <FormattedMessage id="addMember" defaultMessage="Add member" />
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    closeMenu();
                                    onSectionChanged("add", idx);
                                }}
                            >
                                <FormattedMessage id="add_section" defaultMessage="Add section" />
                            </MenuItem>
                        </>
                    )}
                    {active && (
                        <MenuItem
                            onClick={() => {
                                closeMenu();
                                onSectionChanged("remove", idx);
                            }}
                        >
                            <FormattedMessage id="remove_section" defaultMessage="Remove section" />
                        </MenuItem>
                    )}
                </Menu>
            </td>
        </tr>
    );
};

type MemberListProps = {
    memberInputs: FormValues["members"];
    sectionIndexes: boolean[];

    enabled: boolean;
    openNewMemberDialog: (afterIndex?: number) => void;
    memberCanBeSupervisor: (numberId: number) => boolean;

    onDeleteMember: (numberId: number) => void;
    onOverrideNumberChanged: (numberId: number, overrideNumber: boolean) => void;
    onSupervisorChanged: (numberId: number, controllingLine: boolean) => void;
    onAllowEditAddressBookChanged: (numberId: number, allowEditAddressBook: boolean) => void;

    onOrderOrSectionsChanged: (newOrder: number[] | undefined, newSections: boolean[] | undefined) => void;
    shiftSection: (sectionIndex: number, direction: "up" | "down") => boolean[];

    isCallQueue: boolean;
    groupHasAddressBook: boolean;
};

const MemberList = ({
    memberInputs,
    sectionIndexes,
    enabled,
    memberCanBeSupervisor,
    openNewMemberDialog,
    onDeleteMember,
    onOverrideNumberChanged,
    onSupervisorChanged,
    onAllowEditAddressBookChanged,
    onOrderOrSectionsChanged,
    shiftSection,
    isCallQueue,
    groupHasAddressBook,
}: MemberListProps) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(PointerSensor));

    const memberOrder = useMemo(() => {
        const result = new Map<number, number>();
        memberInputs.forEach((m, idx) => result.set(m.numberId, idx));
        return result;
    }, [memberInputs]);

    const handleDragEnd = (event: DragEndEvent) => {
        const {active, over} = event;

        if (active.id === over?.id) return;

        const dragFromIndex = sortableItems.findIndex((item) => item === active.id);
        const dragToIndex = sortableItems.findIndex((item) => item === over.id);

        const direction = dragToIndex > dragFromIndex ? 1 : -1;

        const itemsBetween =
            direction === 1
                ? sortableItems.slice(dragFromIndex + 1, dragToIndex + 1)
                : sortableItems.slice(dragToIndex, dragFromIndex);

        const memberOrdersBetween = itemsBetween
            .filter((item) => !item.startsWith("section-"))
            .map((item) => memberOrder.get(parseInt(item)));
        const sectionIndexesBetween = itemsBetween
            .filter((item) => item.startsWith("section-"))
            .map((item) => parseInt(item.split("-")[1]))
            .filter((index) => sectionIndexes[index]);

        let newOrder: number[] | undefined = undefined;
        let newSections: boolean[] | undefined = undefined;

        if (memberOrdersBetween.length > 0) {
            newOrder = moveItemsInArray(
                Array.from(memberOrder.keys()),
                memberOrder.get(parseInt(active.id as string)),
                direction === 1 ? memberOrdersBetween[memberOrdersBetween.length - 1] : memberOrdersBetween[0],
            );
        }

        if (sectionIndexesBetween.length > 0)
            for (const sectionIndex of sectionIndexesBetween)
                newSections = shiftSection(sectionIndex, direction === 1 ? "up" : "down");

        onOrderOrSectionsChanged(newOrder, newSections);
    };

    function* getComponents() {
        for (const [index, member] of memberInputs.entries()) {
            if (isCallQueue && index !== 0)
                yield (
                    <SectionDividerRow
                        active={sectionIndexes[index]}
                        key={`section-${index}`}
                        idx={index}
                        openNewMemberDialog={openNewMemberDialog}
                        onSectionChanged={(type, index) => {
                            const newSections = structuredClone(sectionIndexes);
                            newSections.splice(index, 1, type === "add");
                            onOrderOrSectionsChanged(undefined, newSections);
                        }}
                    />
                );

            yield (
                <MemberRow
                    key={member.numberId}
                    member={member}
                    enabled={enabled}
                    onDeleteMember={onDeleteMember}
                    onOverrideNumberChanged={onOverrideNumberChanged}
                    onSupervisorChanged={onSupervisorChanged}
                    onAllowEditAddressBookChanged={onAllowEditAddressBookChanged}
                    canBeSupervisor={memberCanBeSupervisor(member.numberId)}
                    isCallQueue={isCallQueue}
                    groupHasAddressBook={groupHasAddressBook}
                />
            );
        }
    }

    const sortableItems = useMemo(() => {
        if (isCallQueue) return memberInputs.flatMap((m, index) => [`section-${index}`, m.numberId.toString()]);
        else return memberInputs.map((m) => m.numberId.toString());
    }, [isCallQueue, memberInputs]);

    return (
        <DndContext
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            sensors={sensors}
            onDragEnd={handleDragEnd}
            collisionDetection={closestCenter}
        >
            <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
                <tbody>{Array.from(getComponents())}</tbody>
            </SortableContext>
        </DndContext>
    );
};

const NoMembersWidget = ({openNewMemberDialog}: {openNewMemberDialog: () => void}) => {
    const {classes} = useStyles();
    return (
        <div className={classes.noMembersContainer}>
            <FormattedAlert
                message={defineMessage({
                    id: "noCallGroupMembers",
                    defaultMessage: 'There are no members! Hit the "Add member" to add member.',
                })}
                severity="info"
            />
            <FormattedButton
                message={defineMessage({id: "addMember", defaultMessage: "Add member"})}
                onClick={() => openNewMemberDialog()}
                style={{marginLeft: "1em"}}
            />
        </div>
    );
};

type MembersTableProps = {
    enabled: boolean;
    dataSource: DataSource;
    form: Form;
    openNewMemberDialog: (afterIndex?: number) => void;
};

const MembersTable = ({dataSource, enabled, form, openNewMemberDialog}: MembersTableProps) => {
    const {classes, cx} = useStyles();

    if (!form.values.members.length) return <NoMembersWidget openNewMemberDialog={openNewMemberDialog} />;

    return (
        <table className={cx({[classes.disabled]: !enabled})}>
            <thead>
                <tr className={cx(classes.row, classes.header)}>
                    <td />
                    <td>
                        <FormattedMessage id="short_number" defaultMessage="Short number" />
                    </td>
                    <td>
                        <FormattedMessage id="display_name" defaultMessage="Display name" />
                    </td>
                    <td>
                        <FormattedMessage id="override_number" defaultMessage="Override number" />
                    </td>
                    {dataSource.isCallQueue && (
                        <>
                            <td>
                                <FormattedMessage id="controlling_line" defaultMessage="Supervisor" />
                            </td>
                            {form.dataSource.groupInfo.hasAddressBook && (
                                <td>
                                    <FormattedMessage
                                        id="allow_edit_address_book"
                                        defaultMessage="Editing address book"
                                    />
                                </td>
                            )}
                        </>
                    )}
                    <td className={classes.actionCell}>
                        <IconButton color="primary" size="small" onClick={() => openNewMemberDialog()}>
                            <FontAwesomeIcon icon="plus-square" />
                        </IconButton>
                    </td>
                </tr>
            </thead>
            <MemberList
                memberInputs={form.values.members}
                sectionIndexes={form.values.sections}
                onDeleteMember={(numberId) => form.removeMember(numberId)}
                onOverrideNumberChanged={(numberId, overrideNumber) => form.updateMember(numberId, {overrideNumber})}
                onSupervisorChanged={(numberId, controllingLine) => form.updateMember(numberId, {controllingLine})}
                onAllowEditAddressBookChanged={(numberId, allowEditAddressBook) =>
                    form.updateMember(numberId, {allowEditAddressBook})
                }
                onOrderOrSectionsChanged={(newOrder, newSections) => {
                    form.updateValues((values) => {
                        if (newOrder !== undefined)
                            values.members = newOrder.map((numberId) =>
                                values.members.find((m) => m.numberId === numberId),
                            );
                        if (newSections !== undefined) values.sections = newSections;
                        return values;
                    });
                }}
                shiftSection={(sectionIndex, direction) => form.getShiftedSections(sectionIndex, direction)}
                enabled={enabled}
                openNewMemberDialog={openNewMemberDialog}
                memberCanBeSupervisor={(numberId) =>
                    form.dataSource
                        .getCapabilityDatas()
                        .find((cd) => cd.numberId === numberId)
                        ?.capabilities.includes(Capability.CcSupervisor) ?? false
                }
                isCallQueue={form.dataSource.isCallQueue}
                groupHasAddressBook={form.dataSource.groupInfo.hasAddressBook}
            />
        </table>
    );
};

const CallGroupMembers = ({
    dataSource,
    setFormMemberCountDiff,
}: {
    dataSource: DataSource;
    setFormMemberCountDiff: (diff: number) => void;
}) => {
    const form = useHookClass(Form, dataSource);
    const [enabled, enable, disable] = useBoolState(true);
    const [isOpenDialog, openDialog, closeDialog] = useBoolState(false);
    const {classes} = useStyles();
    const dispatch = useDispatch();
    const newMemberIdx = useRef<number>(undefined);

    async function submit() {
        disable();
        if (await form.submit()) {
            await dataSource.fetch();
            dispatch(
                showNotification(
                    defineMessage({id: "save_success", defaultMessage: "Data successfully saved"}),
                    "success",
                ),
            );
        } else {
            dispatch(
                showNotification(defineMessage({id: "unexpected_error", defaultMessage: "Unexpected error"}), "error"),
            );
        }
        enable();
    }

    useEffect(() => {
        setFormMemberCountDiff(form.values.members.length - dataSource.groupInfo.members.length);
    }, [dataSource.groupInfo.members.length, form.values.members.length, setFormMemberCountDiff]);

    return (
        <div className={classes.container}>
            <MembersTable
                form={form}
                enabled={enabled}
                openNewMemberDialog={(idx) => {
                    console.debug("Opening new member dialog, idx=", idx);
                    newMemberIdx.current = idx;
                    openDialog();
                }}
                dataSource={dataSource}
            />
            <AddNewMemberDialog
                isCallQueue={form.dataSource.isCallQueue}
                excludedNumberIds={form.values.members.map((inp) => inp.numberId)}
                domainId={dataSource.domainId.current}
                open={isOpenDialog}
                close={closeDialog}
                submit={(members) => form.addNewMembers(members, newMemberIdx.current)}
                groupSize={form.dataSource.groupSize}
                formMemberCount={form.values.members.length}
            />
            <div className={classes.formActionButtons}>
                <FormActionButtons
                    mainAction={submit}
                    secondaryAction={() => form.reset()}
                    enabled={form.hasChanged && enabled}
                />
            </div>
        </div>
    );
};

export const CallGroupMembersPage = ({groupId}: {groupId: number}) => {
    const dataSource = useHookClass(DataSource, groupId);

    // HACK: this is a workaround until we stop using dataSource and form separately (especially in separate components)
    const [formMemberCountDiff, setFormMemberCountDiff] = useState(0);

    if (!dataSource.isInitialized || !dataSource.domainId.current) return <Loading />;

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={dataSource.isCallQueue ? callQueueMembersTitle : callGroupMembersTitle}
                values={{
                    name: dataSource.groupInfo.name,
                    size: dataSource.groupSize,
                    memberCount: dataSource.groupInfo.members.length + formMemberCountDiff,
                }}
            />
            <CallGroupMembers dataSource={dataSource} setFormMemberCountDiff={setFormMemberCountDiff} />
        </div>
    );
};
